import type { BusbarEvidenceResponse, BusbarEvidenceWarning } from "../services/busbarEvidence";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = BusbarEvidenceWarning["code"];

type WarningGroup =
  | "not-met"
  | "review-evidence"
  | "fault-level"
  | "not-evaluated"
  | "declared-evidence";

interface BusbarEvidenceWarningPanelProps {
  result: BusbarEvidenceResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// F1 (2026-10-15): what a stored result of an earlier engine did not record is said on the
// sheet as a warning of the page - not of the engine - listed under "Not evaluated" so that
// a reader (and the printed report) sees that the check was never made for this run.
export const LEGACY_NOTICES = {
  NEUTRAL_HARMONIC_REVIEW_NOT_RECORDED:
    "Neutral harmonic review was not evaluated for this run. Re-run with the current engine " +
    "to include this check.",
  VOLTAGE_DROP_BASIS_NOT_RECORDED:
    "Voltage-drop basis was not recorded for this run. Re-run with the current engine to " +
    "include this check.",
} as const;

type LegacyNoticeCode = keyof typeof LEGACY_NOTICES;

/** The fields the engine of this run did not record, as notice codes. */
export function legacyNotices(result: BusbarEvidenceResponse): LegacyNoticeCode[] {
  const codes: LegacyNoticeCode[] = [];
  if (
    (result.neutral !== null && result.neutral.harmonic_data_available === undefined) ||
    result.neutral_review_required === undefined
  ) {
    codes.push("NEUTRAL_HARMONIC_REVIEW_NOT_RECORDED");
  }
  if (result.trunking !== null && result.trunking.voltage_drop_basis === undefined) {
    codes.push("VOLTAGE_DROP_BASIS_NOT_RECORDED");
  }
  return codes;
}

// Exhaustive maps: a new code without a label or a group is a compile error. A
// busbar evidence warning carries only a code and a message - there is no severity
// and no reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  DESIGN_CURRENT_ABOVE_RATING: "Design current above the rated current",
  ICW_BELOW_PROSPECTIVE_CURRENT: "Icw below the prospective current",
  IPK_BELOW_PEAK_CURRENT: "Ipk below the peak current",
  NEUTRAL_CURRENT_ABOVE_RATING: "Neutral current above the neutral bar rating",
  VOLTAGE_DROP_ABOVE_ALLOWABLE: "Voltage drop above the allowable",
  SITE_AMBIENT_ABOVE_REFERENCE: "Site ambient above the builder's reference ambient",
  FAULT_DURATION_EXCEEDS_ICW_DURATION: "Required fault duration exceeds the Icw duration",
  PEAK_CHECK_NOT_EVALUATED: "Peak withstand check not evaluated",
  RATED_VOLTAGE_BELOW_SYSTEM: "Rated operational voltage below the system voltage",
  NEUTRAL_BELOW_PHASE_NO_HARMONIC_DATA: "Neutral rated below the phase, no harmonic data",
  VOLTAGE_DROP_BASIS_NOT_STATED: "Voltage drop basis not stated",
  PROSPECTIVE_CURRENT_NOT_LINKED: "Prospective current not linked to a fault run",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "Fault run not a maximum three-phase study",
  FAULT_RUN_BUS_MISMATCH: "Fault run bus differs from the study bus",
  BUSBAR_EVIDENCE_MANUFACTURER_DECLARED: "Busbar evidence builder-declared",
};

// Where each code is listed. A check that failed comes first (there is nothing to
// accept), then the evidence the engineer still owes - derating at a site ambient above
// the builder's reference, a fault duration the Icw duration does not cover, a rated
// voltage below the system voltage (IR-001 F1), a neutral rated below the phase without
// stated harmonic data (KES-EL-BUS-002 Rev 0 section 11) or a trunking drop whose basis was
// not stated - then the fault level that is not the
// linked maximum three-phase study of the bus, then the peak check that had no ip to
// be made against, and last the declared-evidence limit that every result carries
// (A24 (g), GAP-022).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  DESIGN_CURRENT_ABOVE_RATING: "not-met",
  ICW_BELOW_PROSPECTIVE_CURRENT: "not-met",
  IPK_BELOW_PEAK_CURRENT: "not-met",
  NEUTRAL_CURRENT_ABOVE_RATING: "not-met",
  VOLTAGE_DROP_ABOVE_ALLOWABLE: "not-met",
  SITE_AMBIENT_ABOVE_REFERENCE: "review-evidence",
  FAULT_DURATION_EXCEEDS_ICW_DURATION: "review-evidence",
  RATED_VOLTAGE_BELOW_SYSTEM: "review-evidence",
  NEUTRAL_BELOW_PHASE_NO_HARMONIC_DATA: "review-evidence",
  VOLTAGE_DROP_BASIS_NOT_STATED: "review-evidence",
  PROSPECTIVE_CURRENT_NOT_LINKED: "fault-level",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "fault-level",
  FAULT_RUN_BUS_MISMATCH: "fault-level",
  PEAK_CHECK_NOT_EVALUATED: "not-evaluated",
  BUSBAR_EVIDENCE_MANUFACTURER_DECLARED: "declared-evidence",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "fault-level", title: "Fault level (review required)" },
  { group: "not-evaluated", title: "Not evaluated" },
  { group: "declared-evidence", title: "Declared evidence" },
];

export function BusbarEvidenceWarningPanel({ result }: BusbarEvidenceWarningPanelProps) {
  // One busbar, so every warning is the study's own, in the engine's order.
  const warnings = result.warnings;
  const notices = legacyNotices(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {notices.length === 0 ? null : (
        <section aria-label="Not evaluated" data-warning-group="not-evaluated" data-legacy-notice="true">
          <h4>
            <StatusIcon tone={GROUP_TONE["not-evaluated"]} size={20} />
            Not evaluated
          </h4>
          <ul>
            {notices.map((code) => (
              <li key={code} data-warning-code={code}>
                <strong>Not recorded by the engine of this run</strong>
                {": "}
                <span>{LEGACY_NOTICES[code]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            // Within a group the engine's order is kept.
            const members = warnings
              .map((warning, index) => ({ ...warning, key: `${warning.code}-${index}` }))
              .filter((warning) => WARNING_CODE_GROUPS[warning.code] === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>{WARNING_CODE_LABELS[warning.code]}</strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default BusbarEvidenceWarningPanel;
